import { all, put, takeLatest, call, select } from 'redux-saga/effects';

import * as actions from '@dragonfruit/redux/navigation';
import { getLocaleCode } from '@dragonfruit/redux/locale';
import { getPreviousRoute } from '@dragonfruit/redux/location';
import { getOrderNumber } from '@dragonfruit/redux/purchase/paymentReceipt';
import config from '@dragonfruit/config';
import { PAYNOW } from '@dragonfruit/constants/paymentTypes';

export function* home(history) {
  const localeCode = yield select(getLocaleCode);
  yield call(history.push, `/${localeCode}`);
}

export function* login(history) {
  const localeCode = yield select(getLocaleCode);
  yield call(history.push, `/${localeCode}/login`);
}

export function* signup(history) {
  const localeCode = yield select(getLocaleCode);
  yield call(history.push, `/${localeCode}/signup`);
}

export function* facebookSignup(history) {
  const localeCode = yield select(getLocaleCode);
  yield call(history.push, `/${localeCode}/facebook-signup`);
}

export function* goToTutorSignupPageFlow(history) {
  const localeCode = yield select(getLocaleCode);
  yield call(history.push, `/${localeCode}/signup-tutor`);
}

export function* goToPurchasePageFlow(history, action) {
  const localeCode = yield select(getLocaleCode);
  const { paymentMethodId } = action;
  yield call(
    history.push,
    `/${localeCode}/purchase/${paymentMethodId}#from=qa`
  );
}
export function* goToChangePlanPageFlow(history, action) {
  const localeCode = yield select(getLocaleCode);
  const { changePlanType, subscriptionId } = action.payload;
  yield call(
    history.push,
    `/${localeCode}/change-plan/${changePlanType}/${subscriptionId}`
  );
}

export function* paymentReceipt(history) {
  const localeCode = yield select(getLocaleCode);
  const orderNumber = yield select(getOrderNumber);
  yield call(history.push, `/${localeCode}/payment-receipt/${orderNumber}`);
}

export function* paymentReceiptForOffline(history, action) {
  const localeCode = yield select(getLocaleCode);
  const { paymentMethodId, paymentType } = action.payload;

  if (paymentType === PAYNOW) {
    return yield call(
      history.push,
      `/${localeCode}/payment-receipt/paynow/${paymentMethodId}`
    );
  }

  yield call(
    history.push,
    `/${localeCode}/payment-receipt/bank-transfer/${paymentMethodId}`
  );
}

export function* premium(history) {
  const localeCode = yield select(getLocaleCode);
  yield call(history.push, `/${localeCode}/premium`);
}

export function* account(history) {
  const localeCode = yield select(getLocaleCode);
  yield call(history.push, `/${localeCode}/account`);
}

export function* accountSubscriptions(history) {
  const localeCode = yield select(getLocaleCode);
  yield call(history.push, `/${localeCode}/account/subscriptions`);
}

export function* accountSubscriptionCancel(history, action) {
  const localeCode = yield select(getLocaleCode);
  const { subscriptionId } = action.payload;
  yield call(
    history.push,
    `/${localeCode}/account/subscription/${subscriptionId}/cancel`
  );
}

export function* verifyTutorEmail(history) {
  const localeCode = yield select(getLocaleCode);
  yield call(history.push, `/${localeCode}/signup-tutor/verify`);
}

export function* auditTutorInformation(history) {
  const localeCode = yield select(getLocaleCode);
  yield call(history.push, `/${localeCode}/signup-tutor/information`);
}

export function* confirmDeleteAccount(history) {
  const localeCode = yield select(getLocaleCode);
  yield call(history.push, `/${localeCode}/delete-account/confirm`);
}

export function* openExternalUrl(action) {
  const {
    payload: { url, newTab },
  } = action;
  window.open(url, newTab ? '_blank' : '_self');
}

export function* goToAccountSetupPageFlow({ payload: { userLocale } }) {
  const getOfficialSiteURL = yield select(actions.generateOfficialSiteURL);
  const url = getOfficialSiteURL('/account/setup', {}, userLocale);

  window.open(url, '_self');
}

export function* coconut(action) {
  const {
    payload: { hash },
  } = action;

  const hashTag = hash ? `#${hash}` : '';

  window.open(`${config.SNAPASK_QA_ROOT_PATH}${hashTag}`, '_self');
}

export function* download() {
  window.open(config.DOWNLOAD_LINK, '_self');
}

export function* previousLocation(history) {
  const previousRoute = yield select(getPreviousRoute);

  if (previousRoute) {
    yield call(history.push, previousRoute);
  } else {
    yield put(actions.home());
  }
}

export function* redirectToPartnerDemoConfirm(history) {
  yield call(history.push, 'partners-demo-confirm');
}

export function* deleteAccount(history) {
  const localeCode = yield select(getLocaleCode);
  yield call(history.push, `/${localeCode}/delete-account`);
}

export function* tutorList(history, action) {
  const localeCode = yield select(getLocaleCode);
  const { filterName = '' } = action.payload;

  yield call(
    history.push,
    `/${localeCode}/tutors${filterName ? `/${filterName}` : ''}`
  );
}

export default function*(context) {
  const { history } = context;

  yield all([
    takeLatest(actions.LOGIN, home, history),
    takeLatest(actions.LOGIN, login, history),
    takeLatest(actions.SIGNUP, signup, history),
    takeLatest(actions.GO_TUTOR_SIGNUP, goToTutorSignupPageFlow, history),
    takeLatest(actions.FACEBOOK_SIGNUP, facebookSignup, history),
    takeLatest(actions.OPEN_EXTERNAL_URL, openExternalUrl),
    takeLatest(actions.GO_ACCOUNT_SETUP_PAGE, goToAccountSetupPageFlow),
    takeLatest(actions.PREVIOUS_LOCATION, previousLocation, history),
    takeLatest(actions.HOME, home, history),
    takeLatest(actions.GO_PURCHASE_PAGE, goToPurchasePageFlow, history),
    takeLatest(actions.PAYMENT_RECEIPT, paymentReceipt, history),
    takeLatest(
      actions.PAYMENT_RECEIPT_FOR_OFFLINE,
      paymentReceiptForOffline,
      history
    ),
    takeLatest(actions.PREMIUM, premium, history),
    takeLatest(actions.ACCOUNT, account, history),
    takeLatest(actions.ACCOUNT_SUBSCRIPTIONS, accountSubscriptions, history),
    takeLatest(
      actions.ACCOUNT_SUBSCRIPTION_CANCEL,
      accountSubscriptionCancel,
      history
    ),
    takeLatest(actions.GO_CHANGE_PLAN_PAGE, goToChangePlanPageFlow, history),
    takeLatest(
      actions.REDIRECT_TO_PARTNER_DEMO_CONFIRM,
      redirectToPartnerDemoConfirm,
      history
    ),
    takeLatest(actions.COCONUT, coconut),
    takeLatest(actions.DOWNLOAD, download),
    takeLatest(actions.CONFIRM_DELETE_ACCOUNT, confirmDeleteAccount, history),
    takeLatest(actions.VERIFY_TUTOR_EMAIL, verifyTutorEmail, history),
    takeLatest(actions.AUDIT_TUTOR_INFROMATION, auditTutorInformation, history),
    takeLatest(actions.DELETE_ACCOUNT, deleteAccount, history),
    takeLatest(actions.TUTOR_LIST, tutorList, history),
  ]);
}



// WEBPACK FOOTER //
// ./app/sagas/navigation.js